import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged, filter } from 'rxjs';
import {
  LawFirmItem,
  LawFirmService,
} from 'src/app/services/law-firm/law-firm.service';
import { EditCaseDialogService } from '../edit-case-dialog.service';
import { PayeeFormFieldComponent } from '../../../shared/payee-form-fields/payee-form-field/payee-form-field.component';
import { PayeeAutocompleteComponent } from '../../../shared/payee-form-fields/payee-autocomplete/payee-autocomplete.component';
import { PayeeMiniIconButtonComponent } from '../../../shared/payee-buttons/payee-mini-icon-button/payee-mini-icon-button.component';
import { PayeeIconComponent } from '../../../shared/payee-icon/payee-icon.component';

@Component({
  selector: 'app-attorney-form',
  templateUrl: './attorney-form.component.html',
  styleUrls: ['./attorney-form.component.scss'],
  imports: [
    PayeeFormFieldComponent,
    PayeeAutocompleteComponent,
    FormsModule,
    ReactiveFormsModule,
    PayeeMiniIconButtonComponent,
    PayeeIconComponent,
  ],
})
export class AttorneyFormComponent implements OnInit, OnDestroy {
  private lawFirmService = inject(LawFirmService);
  private editCaseDialogService = inject(EditCaseDialogService);

  private readonly search = new Subject<string>();
  get form() {
    return this.editCaseDialogService.form.controls.legal_relationship.controls
      .law_firms;
  }

  readonly loading = signal(false);

  readonly lawFirmSearch = new FormControl<LawFirmItem | null>(null);
  lawFirmOptions: LawFirmItem[] = [];

  ngOnInit(): void {
    this.search
      .pipe(
        debounceTime(500),
        filter((s) => !this.loading() && s?.length > 2),
        distinctUntilChanged(),
      )
      .subscribe(async (search) => {
        try {
          this.loading.set(true);

          this.lawFirmOptions = await this.lawFirmService.listLawFirms(search);
        } catch {
          console.error('Error while getting law firms');
        } finally {
          this.loading.set(false);
        }
      });
  }

  ngOnDestroy(): void {
    this.search.complete();
  }

  searchChange(event: string): void {
    this.search.next(event);
  }

  lawFirmSelected(lawFirm?: LawFirmItem) {
    if (lawFirm) {
      const lawFirms = this.form.value as LawFirmItem[];
      if (!lawFirms.find((lf) => lf.id === lawFirm.id)) {
        this.form.push(new FormControl(lawFirm, { nonNullable: true }));
      }
      this.lawFirmOptions = [];
      this.lawFirmSearch.patchValue(null);
    }
  }

  lawFirmDisplayWith(lawFirm?: LawFirmItem): string {
    return lawFirm?.name ?? '';
  }
}
